import { useContext, useMemo } from 'react';
import { Registry } from '@rjsf/utils';
import UWEFormContext from '../../../context/UWEFormContext';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import { FinancialXLSXState, UploadStatus } from './types';
import { findInventorySchedulesInFormData, inventoryUploadStatus } from './util';
import { useReportSchedules } from './useReportSchedules';

export interface ScheduleStatusRow {
  /** Subform name for a regular schedule, schedule title for an inventory schedule. */
  key: string;
  title: string;
  status: UploadStatus;
}

export interface ScheduleStatuses {
  financial: ScheduleStatusRow[];
  inventory: ScheduleStatusRow[];
  completedCount: number;
  totalCount: number;
}

const isFinancialComplete = (status: UploadStatus) => status === 'Uploaded' || status === 'Done';

function readSubformData(root: any, dataPath: string) {
  let node = root;
  for (const segment of dataPath.split('.')) {
    node = node?.[segment];
  }
  return node?.data;
}

function uploadStatusOf(subData: any): UploadStatus | null {
  const uploadStatus = (subData?.uploadStatus ?? '').toLowerCase();
  if (uploadStatus === 'success') return 'Uploaded';
  if (uploadStatus === 'partial') return 'Partial';
  if (uploadStatus === 'error') return 'Error';
  return null;
}

/**
 * Per-schedule filing status for the report being viewed. Live form data wins over the saved
 * entity, which wins over the financial workbook's last recorded statuses.
 */
export function useScheduleStatuses(registry: Registry | undefined, financialXLSX?: FinancialXLSXState | null): ScheduleStatuses {
  const UweFormContext = useContext(UWEFormContext);
  const currentEntity = UweFormContext?.currentEntity;
  const subForms = UweFormContext?.subForms;
  const rootFormData = useSideChannelSubscription(registry?.formContext?.sideChannel, [0]);
  const { scheduleTitles, scheduleNames, inventoryScheduleNames } = useReportSchedules(currentEntity);
  const invObjectMap = registry?.formContext?.formDefinition?.invObjectMapAsMap;

  return useMemo(() => {
    const financialStatus = (title: string, name?: string): UploadStatus => {
      const path = name ? subForms?.[name]?.path : undefined;
      if (path) {
        const dataPath = invObjectMap?.[path] ?? path;
        if (rootFormData) {
          const subData = readSubformData(rootFormData, dataPath);
          if (subData?.markedAsDone) return subData?.uploadStatus === 'success' ? 'Uploaded' : 'Done';
          const status = uploadStatusOf(subData);
          if (status) return status;
        }
        if (currentEntity) {
          const subData = readSubformData(currentEntity, dataPath);
          if (subData?.markedAsDone && !subData?.uploadStatus) return 'Done';
          const status = uploadStatusOf(subData);
          if (status) return status;
        }
      }
      return financialXLSX?.scheduleStatuses?.[title] ?? 'Not Uploaded';
    };

    const financial = scheduleTitles.map((title, i) => ({ key: scheduleNames[i], title, status: financialStatus(title, scheduleNames[i]) }));

    // The inventorySchedules map only lands in the form data once the inventory panel writes to it,
    // so fall back to the report's configured schedule names until then.
    const inventoryStatuses = findInventorySchedulesInFormData(rootFormData);
    const inventoryTitles = inventoryStatuses ? Object.keys(inventoryStatuses) : inventoryScheduleNames;
    const inventory = inventoryTitles.map(title => ({ key: title, title, status: inventoryUploadStatus(inventoryStatuses?.[title]) }));

    const completedCount =
      financial.filter(row => isFinancialComplete(row.status)).length + inventory.filter(row => row.status !== 'Not Uploaded').length;

    return { financial, inventory, completedCount, totalCount: financial.length + inventory.length };
  }, [rootFormData, currentEntity, subForms, invObjectMap, financialXLSX, scheduleTitles, scheduleNames, inventoryScheduleNames]);
}
